import { useMemo, useState } from 'react';
import type { QuickbooksInvoice, MatchedInvoice, MatchMethod } from '@dill/dill-shared';
import { WarningCircle } from 'phosphor-react';
import { formatCurrency, formatDate } from '../utils';
import { DillTable } from '../../../general/Dill';
import type { DillColumn } from '../../../general/Dill';

const MATCH_METHOD_LABELS: Record<MatchMethod, { label: string; className: string }> = {
  EXACT_DOC_NUMBER: { label: 'Exact Doc#', className: 'bg-emerald-50 text-emerald-700' },
  EXACT_DOC_NUMBER_AMOUNT: { label: 'Exact Match', className: 'bg-emerald-100 text-emerald-800' },
  EXACT_PO: { label: 'Exact PO', className: 'bg-blue-50 text-blue-700' },
  EXACT_JOB_NAME: { label: 'Exact Job', className: 'bg-emerald-50 text-emerald-700' },
  EXACT_AMOUNT: { label: 'Exact Amt', className: 'bg-cyan-50 text-cyan-700' },
  FUZZY: { label: 'Fuzzy', className: 'bg-gray-100 text-gray-600' },
  VECTOR_SEARCH: { label: 'AI', className: 'bg-violet-50 text-violet-700' },
  // Grey, not green: the advice named no invoice and we chose these ourselves.
  LAST_STATEMENT: { label: 'From statement', className: 'bg-gray-100 text-gray-600' },
  OPEN_INVOICES: { label: 'Oldest open', className: 'bg-gray-100 text-gray-600' },
  MANUAL: { label: 'Manual', className: 'bg-amber-50 text-amber-700' },
};

type AllocationRow =
  | { _type: 'section'; id: string; label: string; sectionStyle: 'suggested' | 'manual' | 'other' }
  | { _type: 'invoice'; id: string; invoice: QuickbooksInvoice; isSuggested: boolean };

interface AllocationEditorProps {
  invoices: QuickbooksInvoice[];
  suggestedMatches?: MatchedInvoice[];
  allocations: Record<string, number>;
  onAllocationChange: (invoiceId: string, value: string) => void;
  totalAllocated: number;
  paymentAmount: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  title?: string;
  actions?: React.ReactNode;
  /**
   * Offers the unallocated remainder as open credit.
   *
   * Opt-in rather than automatic: an allocation that does not add up is usually a half-finished
   * edit, and posting that silently would place cash the operator had not decided about. The
   * answer is recorded against the record, so a save here and an approval somewhere else — the
   * whole-deposit button, which posts payments nobody is looking at — are the same decision.
   *
   * Passing the handler is what turns the offer on. Every caller whose save or post can leave a
   * remainder needs it: without it the record is saved unapproved and cannot be posted at all.
   */
  onPostRemainderChange?: (value: boolean) => void;
  postRemainderAsCredit?: boolean;
  /**
   * The customer's ledger is still being fetched.
   *
   * Distinguished from an empty list because the two mean opposite things: "this customer has
   * nothing open" is an answer, and "we have not asked yet" is not. Without it the editor claims
   * the former for as long as the request takes, which is what made a slow fetch look like a
   * customer with no invoices to allocate against.
   */
  loading?: boolean;
}

export const AllocationEditor = ({
  invoices,
  suggestedMatches = [],
  allocations,
  onAllocationChange,
  totalAllocated,
  paymentAmount,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search invoices...',
  loading = false,
  title,
  actions,
  onPostRemainderChange,
  postRemainderAsCredit = false,
}: AllocationEditorProps) => {
  const remaining = paymentAmount - totalAllocated;
  // Whether the open-credit offer below covers the whole payment or only what is left of it.
  const nothingAllocated = totalAllocated < 0.01;
  const [matchFilter, setMatchFilter] = useState<string>('all');

  // Build a lookup from suggested matches for enrichment
  const suggestedMap = new Map(suggestedMatches.map((m) => [m.invoiceId, m]));
  /**
   * Only the matcher's own picks, so the "AI Suggested" heading stays true.
   *
   * A saved allocation comes back in `matchedInvoices` like any match, so grouping on that alone
   * put an invoice the operator had typed in under a heading crediting Dill for it — and it read as
   * a fresh suggestion on the next visit rather than a decision already taken.
   */
  const suggestedIds = new Set(
    suggestedMatches.filter((m) => m.matchMethod !== 'MANUAL').map((m) => m.invoiceId)
  );
  const manualIds = new Set(
    suggestedMatches.filter((m) => m.matchMethod === 'MANUAL').map((m) => m.invoiceId)
  );

  // Build flat row array with section headers interspersed, filtered by match type
  const rows: AllocationRow[] = useMemo(() => {
    const suggestedInvoices = invoices.filter((inv) => suggestedIds.has(inv.id));
    const manualInvoices = invoices.filter((inv) => manualIds.has(inv.id));
    const otherInvoices = invoices.filter(
      (inv) => !suggestedIds.has(inv.id) && !manualIds.has(inv.id)
    );
    const result: AllocationRow[] = [];

    if (matchFilter === 'all' || matchFilter === 'suggested') {
      if (suggestedInvoices.length > 0) {
        result.push({
          _type: 'section',
          id: '__section_suggested',
          label: 'AI Suggested',
          sectionStyle: 'suggested',
        });
        suggestedInvoices.forEach((inv) =>
          result.push({ _type: 'invoice', id: inv.id, invoice: inv, isSuggested: true })
        );
      }
    }
    /**
     * Above the open ledger and below the suggestions, because that is the order of standing:
     * a decision already taken outranks one Dill is proposing, and both outrank the rest.
     *
     * Grouped from the saved match method rather than from whether an amount is typed in, so it
     * survives closing the modal — and so it does not resort the table while someone is typing.
     */
    if (matchFilter === 'all' || matchFilter === 'manual') {
      if (manualInvoices.length > 0) {
        result.push({
          _type: 'section',
          id: '__section_manual',
          label: 'Manually Allocated',
          sectionStyle: 'manual',
        });
        manualInvoices.forEach((inv) =>
          result.push({ _type: 'invoice', id: inv.id, invoice: inv, isSuggested: false })
        );
      }
    }
    if (matchFilter === 'all' || matchFilter === 'other') {
      if (otherInvoices.length > 0) {
        result.push({
          _type: 'section',
          id: '__section_other',
          label: 'All Open Invoices',
          sectionStyle: 'other',
        });
        otherInvoices.forEach((inv) =>
          result.push({ _type: 'invoice', id: inv.id, invoice: inv, isSuggested: false })
        );
      }
    }

    return result;
  }, [invoices, suggestedIds, manualIds, matchFilter]);

  const columns: DillColumn<AllocationRow>[] = useMemo(
    () => [
      {
        key: 'docNumber',
        label: 'Invoice #',
        width: 130,
        sortable: false,
        getSearchValue: (row) => {
          if (row._type === 'section') return '';
          const inv = row.invoice;
          return `${inv.docNumber ?? ''} ${inv.poNumber ?? ''} ${
            inv.customerName ?? ''
          }`.toLowerCase();
        },
        render: (_v, row) => {
          if (row._type === 'section') {
            return (
              <span
                className={
                  'text-[11px] font-semibold uppercase tracking-wider ' +
                  (row.sectionStyle === 'suggested' ? 'text-indigo-600' : 'text-gray-400')
                }>
                {row.label}
              </span>
            );
          }
          const inv = row.invoice;
          return (
            <div>
              <p className="text-xs font-medium text-gray-900">{inv.docNumber}</p>
              {inv.poNumber && <p className="text-[11px] text-gray-400">PO: {inv.poNumber}</p>}
            </div>
          );
        },
      },
      {
        key: 'dueDate',
        label: 'Due Date',
        width: 110,
        sortable: false,
        render: (_v, row) => {
          if (row._type === 'section') return null;
          const inv = row.invoice;
          const isOverdue = inv.dueDate ? new Date(inv.dueDate) < new Date() : false;
          return (
            <span
              className={'text-xs ' + (isOverdue ? 'text-red-600 font-medium' : 'text-gray-600')}>
              {inv.dueDate ? formatDate(inv.dueDate) : '—'}
            </span>
          );
        },
      },
      {
        key: 'balance',
        label: 'Balance',
        width: 100,
        numeric: true,
        sortable: false,
        render: (_v, row) => {
          if (row._type === 'section') return null;
          return (
            <span className="text-xs font-semibold text-gray-900">
              {formatCurrency(row.invoice.balance ?? 0)}
            </span>
          );
        },
      },
      {
        key: 'confidence',
        label: 'Confidence',
        width: 90,
        headerAlign: 'center',
        sortable: false,
        render: (_v, row) => {
          if (row._type === 'section') return null;
          const match = suggestedMap.get(row.invoice.id);
          if (!match) return <span className="text-xs text-gray-300">—</span>;
          return (
            <span
              className={`text-xs font-medium ${
                match.confidence >= 0.75
                  ? 'text-emerald-600'
                  : match.confidence >= 0.5
                  ? 'text-amber-600'
                  : 'text-red-500'
              }`}>
              {Math.round(match.confidence * 100)}%
            </span>
          );
        },
      },
      {
        key: 'method',
        label: 'Method',
        width: 90,
        sortable: false,
        render: (_v, row) => {
          if (row._type === 'section') return null;
          const match = suggestedMap.get(row.invoice.id);
          /*
           * An invoice Dill never proposed, carrying money the operator put there by hand.
           *
           * Worth saying rather than leaving as the em dash it used to show. Once the whole ledger
           * is listed, an allocated row and an untouched one look alike apart from a number in a
           * box, and on review — or in Post History afterwards — "who decided this" is the first
           * question. Checked before the match lookup because it is the stronger claim: the
           * operator's own choice outranks any method Dill would have reported.
           */
          if (!match && (allocations[row.invoice.id] ?? 0) > 0) {
            return (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                Manual
              </span>
            );
          }
          if (!match) return <span className="text-xs text-gray-300">—</span>;
          const config = MATCH_METHOD_LABELS[match.matchMethod] ?? MATCH_METHOD_LABELS.FUZZY;
          /*
           * Dill proposed this invoice, but the amount on it is now the operator's.
           *
           * Shown alongside the method rather than replacing it, because both facts still matter on
           * review: how the invoice was found is what says whether the match was right, and the
           * override is what says the figure was not taken on trust. The row already tints amber
           * for this; the tint alone does not survive being described to someone else.
           */
          const isEdited =
            (allocations[row.invoice.id] ?? 0) > 0 &&
            Math.abs((allocations[row.invoice.id] ?? 0) - (match.suggestedAmount ?? 0)) > 0.01;
          return (
            <span className="inline-flex items-center gap-1">
              <span
                className={
                  'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ' +
                  config.className
                }>
                {config.label}
              </span>
              {isEdited && (
                <span
                  title="You changed the suggested amount"
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                  Edited
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: 'allocate',
        label: 'Allocate',
        width: 130,
        sortable: false,
        headerAlign: 'right',
        render: (_v, row) => {
          if (row._type === 'section') return null;
          const inv = row.invoice;
          const match = suggestedMap.get(inv.id);
          return (
            <div className="flex items-center justify-end gap-1">
              <span className="text-xs text-gray-400">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                max={inv.balance ?? 0}
                value={allocations[inv.id] || ''}
                onChange={(e) => onAllocationChange(inv.id, e.target.value)}
                placeholder={
                  match ? formatCurrency(match.suggestedAmount ?? 0).replace('$', '') : '0.00'
                }
                className="w-[90px] text-right text-xs px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          );
        },
      },
    ],
    [allocations, onAllocationChange, suggestedMap]
  );

  const rowClassName = (row: AllocationRow) => {
    if (row._type === 'section') {
      if (row.sectionStyle === 'suggested') return 'bg-indigo-50/50';
      if (row.sectionStyle === 'manual') return 'bg-amber-50/50';
      return 'bg-gray-50/80';
    }
    const inv = row.invoice;
    const allocated = allocations[inv.id] || 0;
    const match = suggestedMap.get(inv.id);
    const isChanged =
      match && allocated > 0 && Math.abs(allocated - (match.suggestedAmount ?? 0)) > 0.01;
    if (isChanged) return 'bg-amber-50/60';
    if (allocated > 0) return 'bg-blue-50/20 hover:bg-blue-50/30';
    if (row.isSuggested) return 'bg-indigo-50/30 hover:bg-indigo-50/50';
    return '';
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {title && <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">{title}</h4>}

      {/* Invoice table */}
      <div className="flex-1">
        {loading && invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <p className="text-sm">Loading this customer&apos;s invoices…</p>
          </div>
        ) : /**
         * "This customer has nothing open" is a fact about the ledger, so it is only said when the
         * ledger is what is empty.
         *
         * `invoices` arrives already filtered by `searchValue`, so a search matching nothing
         * emptied it and took this branch — which replaces the whole table, and the search box
         * lives in the table's toolbar. Typing an invoice number that is not there therefore
         * removed the only control that could undo it: the field, the text in it and every row
         * vanished together, leaving a panel that said the customer had no invoices when a moment
         * earlier it had listed them. The only way out was to close the modal.
         *
         * With a search running, the table renders instead and says "No matching invoices found"
         * over its own empty state — keeping the field, and the query, on screen to be cleared.
         */
        invoices.length === 0 && !searchValue.trim() ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <p className="text-sm">No open invoices for this customer.</p>
          </div>
        ) : (
          <DillTable<AllocationRow>
            columns={columns}
            data={rows}
            rowKey={(row) => row.id}
            maxHeight="360px"
            rowClassName={rowClassName}
            emptyMessage="No matching invoices found."
            hoverVariant="default"
            scrollX
            search
            searchValue={searchValue}
            onSearch={onSearchChange}
            searchPlaceholder={searchPlaceholder}
            toolbarLeft={
              <select
                value={matchFilter}
                onChange={(e) => setMatchFilter(e.target.value)}
                className="h-8 px-2 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 outline-none focus:border-blue-300 transition-colors">
                <option value="all">All Invoices</option>
                <option value="suggested">AI Suggested</option>
                <option value="manual">Manually Allocated</option>
                <option value="other">Not Suggested</option>
              </select>
            }
          />
        )}
      </div>

      {/* Summary */}
      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 mt-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-3">
              <span className="text-gray-500">Payment:</span>
              <span className="font-bold text-gray-900">{formatCurrency(paymentAmount)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-500">Allocated:</span>
              <span className="font-bold text-blue-600">{formatCurrency(totalAllocated)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-500">Remaining:</span>
              <span
                className={
                  'font-bold ' +
                  (Math.abs(remaining) < 0.01
                    ? 'text-emerald-600'
                    : remaining < 0
                    ? 'text-red-600'
                    : 'text-amber-600')
                }>
                {/* `|| 0` because +(-1e-13).toFixed(2) is -0, which still formats as "-$0.00". */}
                {formatCurrency(+remaining.toFixed(2) || 0)}
              </span>
            </div>
          </div>
          {actions}
        </div>
        {remaining < -0.01 && (
          <p className="text-xs text-red-600 flex items-center gap-1 mt-2">
            <WarningCircle size={12} weight="fill" />
            Over-allocated by {formatCurrency(Math.abs(remaining))}
          </p>
        )}
        {/*
          Only for a genuine shortfall. An over-allocation cannot be resolved this way — there is
          no remainder, there is money the payment does not have.

          Offered with nothing allocated too: a customer with no open invoice has no other answer,
          and hiding it left those payments stuck in Exceptions with no way to post them.
        */}
        {onPostRemainderChange && remaining > 0.01 && (
          <label className="flex items-start gap-2 mt-2.5 pt-2.5 border-t border-gray-200 cursor-pointer group">
            <input
              type="checkbox"
              checked={postRemainderAsCredit}
              onChange={(event) => onPostRemainderChange(event.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-xs text-gray-700 leading-relaxed">
              {nothingAllocated ? 'Post the whole payment of ' : 'Post the remaining '}
              <span className="font-semibold text-amber-700">{formatCurrency(remaining)}</span> as
              open credit on the customer&apos;s account
              <span className="block text-[11px] text-gray-500 mt-0.5">
                {nothingAllocated
                  ? 'No invoice is paid by this: the full amount sits on the account for a later one. Use this when there is nothing open to apply it to — not to get past an allocation you have not finished.'
                  : 'One post, both parts: the invoices above are paid and the remainder sits on the account for a later invoice. Use this for a short-pay or a rounding difference rather than leaving the whole payment unposted.'}
              </span>
            </span>
          </label>
        )}
      </div>
    </div>
  );
};
